import { useCallback, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useAuth } from "@/shared/hooks/useAuth";
import { invalidateUploadedDocumentQueries } from "@/shared/utils/cacheInvalidation";
import { generateQRWithLogo, getQRFallbackUrl } from "@/shared/utils/generateQRWithLogo";

interface UseMobileUploadSessionOptions {
  active: boolean;
  expiryMinutes?: number;
  maxUploads?: number;
  documentType?: string;
  onUploadReceived?: () => void;
}

export function useMobileUploadSession({
  active,
  expiryMinutes = 15,
  maxUploads,
  documentType = "death_certificate",
  onUploadReceived,
}: UseMobileUploadSessionOptions) {
  const { person, user } = useAppContext();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const estateId = person?.estate_id;

  const [sessionId, setSessionId] = useState("");
  const [mobileUrl, setMobileUrl] = useState("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<{ fileName: string }[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const onUploadReceivedRef = useRef(onUploadReceived);
  onUploadReceivedRef.current = onUploadReceived;
  const hasNotifiedRef = useRef(false);

  const notifyOnce = useCallback(() => {
    if (hasNotifiedRef.current) return;
    hasNotifiedRef.current = true;
    onUploadReceivedRef.current?.();
  }, []);

  const cleanup = useCallback(
    (sid: string, count: number) => {
      if (sid) {
        supabase
          .from("upload_sessions")
          .delete()
          .eq("session_id", Number(sid))
          .then(undefined, (e) => console.error("Failed to delete upload session", e));
      }
      channelRef.current?.unsubscribe();
      channelRef.current = null;
      setSessionId("");
      setMobileUrl("");
      setQrCodeDataUrl("");
      if (count > 0) {
        notifyOnce();
      }
      setUploadCount(0);
      setUploadedFiles([]);
      hasNotifiedRef.current = false;
    },
    [notifyOnce],
  );

  useEffect(() => {
    let cancelled = false;
    if (!active || sessionId) return;
    hasNotifiedRef.current = false;

    (async () => {
      try {
        const at = await getAccessToken?.();
        const expiresAt = new Date(Date.now() + expiryMinutes * 60 * 1000).toISOString();
        const { data, error } = await supabase
          .from("upload_sessions")
          .insert({
            estate_id: person?.estate_id || null,
            created_by_user_id: user?.user_id || null,
            status: "active",
            expires_at: expiresAt,
            access_token: at || null,
            ...(maxUploads !== undefined && { remaining_uploads: maxUploads }),
          })
          .select("session_id")
          .single();
        if (error) throw error;

        const sid = String(data.session_id);

        if (cancelled) {
          supabase
            .from("upload_sessions")
            .delete()
            .eq("session_id", Number(sid))
            .then(undefined, () => {});
          return;
        }

        setSessionId(sid);
        setMobileUrl(`${window.location.origin}/mobile-upload?sid=${sid}`);
      } catch (e) {
        console.error("Failed to initialize mobile upload session", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    active,
    sessionId,
    getAccessToken,
    person?.estate_id,
    user?.user_id,
    expiryMinutes,
    maxUploads,
  ]);

  useEffect(() => {
    if (!mobileUrl) return;
    let cancelled = false;

    generateQRWithLogo(mobileUrl)
      .then((dataUrl) => {
        if (!cancelled) setQrCodeDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQrCodeDataUrl(getQRFallbackUrl(mobileUrl));
      });

    return () => {
      cancelled = true;
    };
  }, [mobileUrl]);

  const saveDocumentMetadata = useCallback(
    async (fileKey: string, fileName: string) => {
      if (!estateId) throw new Error("Missing estate ID");

      const accessToken = await getAccessToken?.();
      if (!accessToken) throw new Error("Missing access token");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/metadata`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            file_key: fileKey,
            file_name: fileName,
            file_type: "application/pdf",
            file_size: 0,
            document_type: documentType,
            upload_session_id: sessionId ? Number(sessionId) : undefined,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to save document metadata: ${response.status}`);
      }

      invalidateUploadedDocumentQueries(queryClient, estateId);
      queryClient.invalidateQueries({ queryKey: ["estate_documents", estateId] });
    },
    [estateId, getAccessToken, queryClient, sessionId, documentType],
  );

  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase.channel(`mobile_upload:${sessionId}`);

    channel.on(
      "broadcast",
      { event: "file_uploaded" },
      (payload: {
        payload: {
          fileKey: string;
          fileName: string;
          contentType?: string;
        };
      }) => {
        const { fileKey, fileName } = payload?.payload ?? {};
        (async () => {
          try {
            await saveDocumentMetadata(fileKey, fileName);
            setUploadCount((prev) => prev + 1);
            setUploadedFiles((prev) => [...prev, { fileName }]);
          } catch (e) {
            console.error("Failed to save document metadata for mobile upload:", e);
          }
        })();
      },
    );

    channel.subscribe();
    channelRef.current = channel;

    return () => {
      channel?.unsubscribe();
      channelRef.current = null;
    };
  }, [sessionId, saveDocumentMetadata]);

  // Auto-close when max uploads reached
  useEffect(() => {
    if (maxUploads === undefined || uploadCount < maxUploads) return;
    const timeout = setTimeout(() => notifyOnce(), 1500);
    return () => clearTimeout(timeout);
  }, [uploadCount, maxUploads, notifyOnce]);

  // Cleanup when deactivated or on unmount
  const sessionRef = useRef(sessionId);
  const uploadCountRef = useRef(uploadCount);
  sessionRef.current = sessionId;
  uploadCountRef.current = uploadCount;

  const prevActiveRef = useRef(active);
  useEffect(() => {
    if (prevActiveRef.current && !active && sessionId) {
      cleanup(sessionId, uploadCount);
    }
    prevActiveRef.current = active;
  }, [active, sessionId, uploadCount, cleanup]);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        cleanup(sessionRef.current, uploadCountRef.current);
      }
    };
  }, [cleanup]);

  const qrSrc = qrCodeDataUrl || (mobileUrl ? getQRFallbackUrl(mobileUrl) : "");

  return { qrSrc, mobileUrl, uploadCount, uploadedFiles, expiryMinutes };
}
